import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, RotateCcw, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getFieldPositionStyles } from './coverFieldStyles';
import { CoverFrontPanel } from './CoverFrontPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-shrink a spine text field's font-size so that its content fits within
 * the container in both inline and block axes (handles ``writing-mode:
 * vertical-rl``). Allows wrapping to a second column.
 *
 * Reads ``data-base-font-size`` from the element so the same node can be
 * re-measured when scale changes without losing the original target size.
 */
export const useAutoShrinkSpineFont = (ref, text, baseFontSize) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to the target size before measuring so re-renders shrink from
    // baseline (not from the previously-shrunk size).
    el.style.fontSize = `${baseFontSize}px`;
    const minFs = Math.max(baseFontSize * 0.5, 5);
    let fs = baseFontSize;
    let safety = 0;
    while (
      safety < 60 &&
      fs > minFs &&
      (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1)
    ) {
      fs -= 0.3;
      el.style.fontSize = `${fs}px`;
      safety += 1;
    }
  }, [ref, text, baseFontSize]);
};

/**
 * Single text field on the spine — vertical writing, auto-shrinks to fit,
 * allows wrapping to a second column when the title is long. Shared by the
 * admin template editor and the promoter cover preview so both renders
 * stay in perfect sync.
 */
export const SpineTextField = ({
  cfg,
  value,
  baseFontSize,
  isTitle,
  // When provided, overrides the fixed 60%/35% maxHeight. Used by
  // CoverSpread to compute the available vertical room between this
  // field and the next one in the spine — so a long title can NEVER
  // overflow into the brand line below.
  maxHeightPct,
  extraClassName = '',
  extraStyle = {},
  draggable = false,
  onMouseDown,
}) => {
  const ref = useRef(null);
  useAutoShrinkSpineFont(ref, value, baseFontSize);
  const computedMaxHeight =
    maxHeightPct != null ? `${Math.max(6, maxHeightPct)}%` : (isTitle ? '60%' : '35%');
  return (
    <div
      ref={ref}
      className={extraClassName}
      style={{
        position: 'absolute',
        left: `${cfg.x ?? 50}%`,
        top: `${cfg.y ?? 50}%`,
        transform: 'translate(-50%, 0)',
        writingMode: 'vertical-rl',
        textOrientation: 'mixed',
        fontSize: baseFontSize,
        color: cfg.color || '#fff',
        fontWeight: cfg.fontWeight || 'normal',
        fontFamily: cfg.fontFamily || 'sans-serif',
        textTransform: cfg.textTransform || 'none',
        letterSpacing: cfg.letterSpacing || 'normal',
        textAlign: cfg.textAlign || 'center',
        lineHeight: 1.2,
        // Dynamic per-field max-height (computed by the parent based on
        // the distance to the next field) so the title can't overlap the
        // branding even with a very long book name.
        maxHeight: computedMaxHeight,
        // Cap horizontal growth to ~90% of the spine so wrapped columns
        // don't escape the panel.
        maxWidth: '90%',
        // Wrap is allowed so long titles flow into a second column instead
        // of being clipped.
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        overflow: 'hidden',
        padding: '2px',
        ...extraStyle,
      }}
      onMouseDown={onMouseDown}
    >
      {value}
    </div>
  );
};

/**
 * Auto-shrink a regular (horizontal) front/back cover text field with a
 * two-pass strategy:
 *
 *   1. Force single-line (white-space: nowrap) and shrink until it fits
 *      horizontally OR we hit 70 % of the base font.
 *   2. If 70 % wasn't enough, re-enable wrapping and continue shrinking
 *      until the wrapped text fits vertically (or 50 % floor).
 *
 * This is what the promoter actually expects: short/medium texts stay on
 * a single line at the designed size; only truly long titles wrap, and
 * even then the font is reduced so they don't push other fields out.
 */
export const useAutoShrinkFont = (ref, text, baseFontSize) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset to base style; nowrap on first pass.
    el.style.fontSize = `${baseFontSize}px`;
    el.style.whiteSpace = 'nowrap';
    let fs = baseFontSize;
    const noWrapFloor = Math.max(baseFontSize * 0.7, 7);
    let safety = 0;
    while (
      safety < 60 &&
      fs > noWrapFloor &&
      el.scrollWidth > el.clientWidth + 1
    ) {
      fs -= 0.4;
      el.style.fontSize = `${fs}px`;
      safety += 1;
    }
    // If still doesn't fit horizontally at the nowrap floor, fall back
    // to normal wrapping and shrink the wrapped result vertically.
    if (el.scrollWidth > el.clientWidth + 1) {
      el.style.whiteSpace = 'normal';
      const hardFloor = Math.max(baseFontSize * 0.5, 6);
      safety = 0;
      while (
        safety < 60 &&
        fs > hardFloor &&
        el.scrollHeight > el.clientHeight + 1
      ) {
        fs -= 0.4;
        el.style.fontSize = `${fs}px`;
        safety += 1;
      }
    }
  }, [ref, text, baseFontSize]);
};


/**
 * Single positioned text field on the front or back cover. Auto-shrinks
 * the font-size to fit within the available height (computed by the
 * parent based on the distance to the next field below). Used by both
 * the promoter preview and the admin editor.
 */
export const CoverTextField = ({ cfg, value, baseFontSize, maxHeightPct, extraStyle = {}, onMouseDown, className = '', dataTestId }) => {
  const ref = useRef(null);
  useAutoShrinkFont(ref, value, baseFontSize);
  const pos = getFieldPositionStyles(cfg);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        position: 'absolute',
        ...pos,
        fontSize: baseFontSize,
        color: cfg.color || '#fff',
        fontFamily: cfg.fontFamily || 'sans-serif',
        fontWeight: cfg.fontWeight || 'normal',
        textTransform: cfg.textTransform || 'none',
        letterSpacing: cfg.letterSpacing || 'normal',
        lineHeight: 1.3,
        maxHeight: maxHeightPct != null ? `${Math.max(6, maxHeightPct)}%` : '40%',
        overflow: 'hidden',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        padding: '2px 4px',
        ...extraStyle,
      }}
      data-testid={dataTestId}
      onMouseDown={onMouseDown}
    >
      {value}
    </div>
  );
};

/**
 * Given a fields object {key → cfg}, return a map {key → maxHeightPct}
 * computed as the distance to the next field below (sorted by y). Used
 * by CoverSpread to prevent overlap: the title's max-height is exactly
 * the gap to the branding line, etc.
 *
 * `extraOverrides` lets the caller override the y used for ordering
 * (e.g. when book.spine_overrides shifts a field's position).
 */
const computeMaxHeightMap = (fields, overrides = {}) => {
  const entries = Object.entries(fields).map(([k, cfg]) => {
    const ovr = overrides[k] || {};
    const y = ovr.y !== undefined && ovr.y !== null ? ovr.y : (cfg.y ?? 50);
    return { key: k, y };
  });
  entries.sort((a, b) => a.y - b.y);
  const out = {};
  for (let i = 0; i < entries.length; i += 1) {
    const cur = entries[i];
    const next = entries[i + 1];
    // Reserve a 2 % gap so adjacent texts don't touch.
    const limit = next ? next.y - cur.y - 2 : 100 - cur.y - 2;
    out[cur.key] = Math.max(8, limit);
  }
  return out;
};

export { computeMaxHeightMap };


/**
 * Track the live pixel width of a container ref. Used by responsive
 * cover previews so we can compute a px-based baseFontSize that the
 * JS auto-shrink hook can measure against.
 */
export const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, [ref]);
  return width;
};

/**
 * Auto-fit a stack of cover text fields inside a known container size.
 * Used both in the inline preview and in the catalog thumbnails so the
 * rendering math is shared. Returns the rendered text-field nodes.
 *
 * `refWidth` is the design-time reference width (380 by default — the
 * value the admin uses when picking font sizes). The container actual
 * width gives us a scale factor; the JS measuring inside CoverTextField
 * then shrinks the font further if the content still overflows.
 */
export const AutoFitCoverFields = ({ fields, fieldValues, containerWidth, refWidth = 380 }) => {
  if (!fields || Object.keys(fields).length === 0) return null;
  const maxHeights = computeMaxHeightMap(fields);
  if (!containerWidth) return null;  // wait for first measure
  return Object.entries(fields).map(([key, cfg]) => (
    <CoverTextField
      key={key}
      cfg={cfg}
      value={fieldValues[key] || ''}
      baseFontSize={(cfg.fontSize || 12) * (containerWidth / refWidth)}
      maxHeightPct={maxHeights[key]}
      className="z-10"
    />
  ));
};


/**
 * Render the 3-panel spread (back cover + spine + front cover) of a book.
 *
 * Used in two places:
 *  - Inline in the BookDetail "Diseño de portada" section (scale=0.55) so
 *    the promoter sees the full preview the moment they pick a template.
 *  - Inside the CoverFullPreview dialog (scale=1) for a larger view.
 *
 * Auto-triggers ``/generate-back-reviews`` on first mount when the book
 * has generated_content but no reviews yet, so the back cover shows
 * something real instead of an empty placeholder.
 */
export const CoverSpread = ({ book, bookId, coverTemplates, selectedCoverTemplate, photoVersion, photoAdjust: photoAdjustProp, scale = 1, onReviewsUpdated }) => {
  const [reviews, setReviews] = useState(book?.back_cover_reviews || []);
  const [generating, setGenerating] = useState(false);
  const [autoTriedRef] = useState({ tried: false });

  useEffect(() => {
    if (book?.back_cover_reviews) setReviews(book.back_cover_reviews);
  }, [book?.back_cover_reviews]);

  // Auto-generate reviews on first mount if missing and book is ready.
  // Only tries once per component instance to avoid retry storms.
  useEffect(() => {
    if (autoTriedRef.tried) return;
    if (!book?.generated_content) return;
    if (reviews && reviews.length > 0) return;
    autoTriedRef.tried = true;
    (async () => {
      setGenerating(true);
      try {
        const res = await axios.post(
          `${API_URL}/api/books/${bookId}/generate-back-reviews`,
          {},
          { withCredentials: true },
        );
        setReviews(res.data.reviews || []);
        onReviewsUpdated?.(res.data.reviews || []);
      } catch {
        // silent — user can retry from the button
      } finally {
        setGenerating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.generated_content, bookId]);

  const currentTmpl = coverTemplates?.find(t => t.template_id === selectedCoverTemplate);
  const snapshot = book?.cover_snapshot || {};
  const fc = currentTmpl?.fields_config || snapshot.fields_config || {};
  const spineConfig = currentTmpl?.spine_config || snapshot.spine_config || {};
  const backConfig = currentTmpl?.back_config || snapshot.back_config || {};

  // Full name: "Nombre Apellido" — the protagonist's name on the cover
  // must be complete. Fallback to just the first name if no last name.
  const recipientFirst = book?.profile?.recipient_name || 'Nombre';
  const recipientLast = book?.profile?.recipient_last_name || '';
  const recipientFullName = `${recipientFirst} ${recipientLast}`.trim();

  const bookTitle = book?.title || 'Titulo del libro';

  const fieldValues = {
    protagonist_name: recipientFullName,
    subtitle: book?.subtitle || 'Un libro para',
    title: bookTitle,
    branding: 'A GIFT OF MEMORIES',
  };

  const bgType = currentTmpl?.background_type || snapshot.background_type || 'photo';
  const hasTemplateBackground = bgType === 'custom' && (currentTmpl?.preview_image_path || snapshot.preview_image_path);
  const photoFrame = currentTmpl?.photo_frame || snapshot.photo_frame || null;
  const isFramed = hasTemplateBackground && photoFrame?.type === 'framed';

  const templateBgSrc = hasTemplateBackground && currentTmpl
    ? `${API_URL}/api/cover-templates/${currentTmpl.template_id}/preview-image`
    : null;
  const protagonistSrc = book?.cover_photo_path
    ? `${API_URL}/api/books/${bookId}/cover-photo?v=${photoVersion}`
    : (book?.protagonist_photo?.photo_id
      ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}`
      : 'https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=600&h=800&fit=crop&crop=face');
  const photoSrc = isFramed ? (templateBgSrc || protagonistSrc) : protagonistSrc;

  // Prefer the live state passed by BookDetail (drag updates immediately)
  // over the persisted book.cover_photo_adjustments. Falls back to the
  // saved value when used as a standalone (e.g. inside CoverFullPreview
  // dialog opened from another route).
  const photoAdjust = photoAdjustProp || book?.cover_photo_adjustments || { zoom: 1, offsetX: 0, offsetY: 0 };
  const photoStyle = !isFramed && book?.cover_photo_path ? {
    transform: `scale(${photoAdjust.zoom})`,
    transformOrigin: `${50 - photoAdjust.offsetX}% ${50 - photoAdjust.offsetY}%`,
  } : {};

  const coverBgColor = (isFramed && photoFrame?.bg_color) ? photoFrame.bg_color : '#1a1a1a';
  const spineBg = spineConfig.background_color || '#1a1a1a';
  const backBg = backConfig.background_color || '#ffffff';

  const generateReviews = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/books/${bookId}/generate-back-reviews`, {}, { withCredentials: true });
      setReviews(res.data.reviews);
      onReviewsUpdated?.(res.data.reviews || []);
      toast.success('Reseñas regeneradas');
    } catch {
      toast.error('Error al generar reseñas');
    } finally {
      setGenerating(false);
    }
  };

  // Base reference dimensions for a single panel at scale=1.
  const PANEL_W = 280;
  const PANEL_H = 400;
  const SPINE_W = 40;
  const REF_W = 380;

  const w = Math.round(PANEL_W * scale);
  const h = Math.round(PANEL_H * scale);
  const sw = Math.round(SPINE_W * scale);

  const getFrameClipPath = (shape) => {
    if (shape === 'circle') return 'circle(50% at 50% 50%)';
    if (shape === 'oval') return 'ellipse(50% 50% at 50% 50%)';
    return 'none';
  };

  const renderFramedPhoto = () => {
    if (!isFramed || !protagonistSrc) return null;
    const fr = photoFrame;
    const left = (fr.x || 50) - (fr.width || 40) / 2;
    const top = (fr.y || 40) - (fr.height || 50) / 2;
    const clipPath = getFrameClipPath(fr.shape);
    const borderRadius = fr.shape === 'rectangle' ? '6px' : '0';
    const framedStyle = book?.cover_photo_path ? {
      transform: `scale(${photoAdjust.zoom})`,
      transformOrigin: `${50 - photoAdjust.offsetX}% ${50 - photoAdjust.offsetY}%`,
    } : {};
    return (
      <div className="absolute z-10 overflow-hidden" style={{
        left: `${left}%`, top: `${top}%`,
        width: `${fr.width || 40}%`, height: `${fr.height || 50}%`,
        clipPath: clipPath !== 'none' ? clipPath : undefined,
        borderRadius,
        transform: fr.rotation ? `rotate(${fr.rotation}deg)` : undefined,
        background: '#000',
      }}>
        <img src={protagonistSrc} alt="" className="w-full h-full object-contain" style={framedStyle} />
      </div>
    );
  };

  // Scaled text sizes (linear with scale)
  const _fz = (px) => Math.max(Math.round(px * scale * 10) / 10, 5);
  const numReviews = backConfig.num_reviews || 4;
  // When the panel is small we render fewer reviews so the text never
  // overlaps the bottom branding block.
  const reviewsToShow = scale < 0.7 ? Math.min(numReviews, 3) : numReviews;

  // ---------------------------------------------------------------------
  // Spine + Back values (mirror admin editor — see CoverTemplateEditor.js)
  // Fields live under ``spineConfig.fields`` and ``backConfig.fields``,
  // NOT at the top level. Each entry has x/y/fontSize/color/fontWeight/
  // fontFamily/textTransform/letterSpacing/textAlign.
  // ---------------------------------------------------------------------
  const spineFields = spineConfig.fields || {};
  const spineFieldValues = {
    protagonist_name: recipientFullName,
    title: bookTitle,
    branding: backConfig.branding_text || 'A GIFT OF MEMORIES',
  };
  const backFields = backConfig.fields || {};
  const backFieldValues = {
    heading: `Elogios para ${bookTitle}`,
    branding: backConfig.branding_text || 'A GIFT OF MEMORIES',
    website: backConfig.website_text || 'www.agiftofmemories.com',
  };

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center items-stretch gap-0" data-testid="cover-full-spread">
        {/* BACK COVER */}
        <div className="flex-shrink-0" style={{ width: w, height: h, background: backBg, borderRadius: '6px 0 0 6px', border: '1px solid #e5e7eb', borderRight: 'none', position: 'relative', overflow: 'hidden' }}>
          {/* Positioned text fields (heading, branding, website) — mirror
              admin editor's render exactly (line ~396 of CoverTemplateEditor). */}
          {Object.entries(backFields).map(([key, cfg]) => {
            const pos = getFieldPositionStyles(cfg);
            return (
            <div
              key={key}
              className="absolute"
              style={{
                ...pos,
                fontSize: (cfg.fontSize || 12) * (w / REF_W),
                color: cfg.color || '#333',
                fontWeight: cfg.fontWeight || 'normal',
                fontFamily: cfg.fontFamily || 'sans-serif',
                textTransform: cfg.textTransform || 'none',
                letterSpacing: cfg.letterSpacing || 'normal',
                lineHeight: 1.3,
                padding: '2px 4px',
              }}
            >
              {backFieldValues[key] || ''}
            </div>
          );})}

          {/* Reviews block — same y-band as the admin editor (15% to 82%). */}
          <div className="absolute" style={{ top: '15%', left: 0, right: 0, bottom: '18%', overflow: 'hidden', padding: `0 ${Math.round(16 * scale)}px` }}>
            {reviews.length > 0 ? reviews.slice(0, reviewsToShow).map((r, i) => (
              <div key={i} className="mb-2" style={{ fontSize: _fz(10), lineHeight: 1.4 }}>
                <p style={{ color: backConfig.review_color || '#333' }}>"{r.text}"</p>
                <p className="font-bold mt-0.5" style={{ color: backConfig.reviewer_color || '#666', fontSize: _fz(9) }}>{r.source}</p>
              </div>
            )) : (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-center">
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#999' }} />
                    <p style={{ fontSize: _fz(9), color: '#666' }}>Generando reseñas con IA…</p>
                  </>
                ) : (
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={generateReviews} disabled={generating} data-testid="generate-reviews-btn">
                    <RotateCcw className="w-3 h-3" />
                    Generar reseñas
                  </Button>
                )}
              </div>
            )}
          </div>

          {reviews.length > 0 && (
            <button
              onClick={generateReviews}
              disabled={generating}
              className="absolute top-2 right-2 p-1 rounded bg-white/80 hover:bg-white shadow-sm transition-colors z-30"
              title="Regenerar reseñas"
              data-testid="regenerate-reviews-btn"
            >
              {generating ? <Loader2 className="w-3 h-3 animate-spin text-gray-500" /> : <RotateCcw className="w-3 h-3 text-gray-500" />}
            </button>
          )}
        </div>

        {/* SPINE — auto-fit text fields. Each field's max-height is the
            distance to the next field below, so a long title can never
            overflow into the branding. */}
        <div className="flex-shrink-0" style={{ width: sw, height: h, background: spineBg, position: 'relative', overflow: 'hidden' }}>
          {(() => {
            const maxHeights = computeMaxHeightMap(spineFields);
            return Object.entries(spineFields).map(([key, cfg]) => (
              <SpineTextField
                key={key}
                cfg={cfg}
                value={spineFieldValues[key] || ''}
                baseFontSize={(cfg.fontSize || 8) * (sw / SPINE_W)}
                isTitle={key === 'title'}
                maxHeightPct={maxHeights[key]}
              />
            ));
          })()}
        </div>

        {/* FRONT COVER — uses the single source of truth (CoverFrontPanel)
            so the spread view matches the inline preview exactly. */}
        <div className="flex-shrink-0" style={{ width: w, height: h, borderRadius: '0 6px 6px 0', position: 'relative', overflow: 'hidden', containerType: 'inline-size' }}>
          <CoverFrontPanel
            template={currentTmpl}
            bookId={bookId}
            protagonistPhotoUrl={book?.cover_photo_path
              ? `${API_URL}/api/books/${bookId}/cover-photo`
              : (book?.protagonist_photo?.photo_id ? `${API_URL}/api/anecdote-photo/${book.protagonist_photo.photo_id}` : null)}
            protagonistPhotoVersion={photoVersion}
            fieldValues={fieldValues}
            photoAdjust={photoAdjust}
          />

          {(backConfig.show_badge !== false) && (
            <div className="absolute rounded-full border-2 border-white/80 flex flex-col items-center justify-center bg-white/90 z-20" style={{ top: Math.round(12 * scale), right: Math.round(12 * scale), width: Math.round(48 * scale), height: Math.round(48 * scale), lineHeight: 1 }}>
              <span style={{ fontSize: _fz(7), fontWeight: 'bold', color: '#dc2626' }}>2026</span>
              <span style={{ fontSize: _fz(5), fontWeight: 'bold', color: '#1f2937', lineHeight: 1 }}>BEST SELLER</span>
              <span style={{ fontSize: _fz(5), fontWeight: 'bold', color: '#1f2937', lineHeight: 1 }}>NACIONAL</span>
            </div>
          )}
        </div>
      </div>

      {/* Labels */}
      <div className="flex justify-center items-center gap-0 mt-2 text-muted-foreground" style={{ fontSize: _fz(10) }}>
        <div style={{ width: w, textAlign: 'center' }}>Contraportada</div>
        <div style={{ width: sw, textAlign: 'center' }}>Lomo</div>
        <div style={{ width: w, textAlign: 'center' }}>Portada</div>
      </div>
    </div>
  );
};

const CoverFullPreview = ({ book, bookId, coverTemplates, selectedCoverTemplate, photoVersion, photoAdjust, open, onClose, onReviewsUpdated }) => {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-6" data-testid="cover-full-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            Vista completa del libro
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <CoverSpread
            book={book}
            bookId={bookId}
            coverTemplates={coverTemplates}
            selectedCoverTemplate={selectedCoverTemplate}
            photoVersion={photoVersion}
            photoAdjust={photoAdjust}
            scale={1}
            onReviewsUpdated={onReviewsUpdated}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoverFullPreview;
